import React, { useCallback, useEffect, useState, useRef } from "react";
import CommonLayout from "../../../Components/CommonLayout/CommonLayout";
import BasicTable from "../../../Components/CommonTable/BasicTable/BasicTable";
import earningApis from "../../../App/api/earning.apis";
import { useFetchAll } from "../../../custom/FetchApiHook";
import { formattedAmount } from "../../../utils/helpers/Functions.helper";
import { useParams, useNavigate } from "react-router-dom";
import OrderApi from "../../../App/api/order.apis";
import { PaystackButton } from "react-paystack";
import { filters } from "../Orders/Column";
import { toast } from "react-toastify";

// ─── Constants ────────────────────────────────────────────────────────────────

const DELIVERY_STATUS_OPTIONS = [
  { label: "All Status", value: "" },
  { label: "Waiting", value: "WAITING" },
  { label: "Accepted", value: "ACCEPTED" },
  { label: "Assigned", value: "ASSIGNED" },
  { label: "Assigned to Delivery Man", value: "ASSIGNED_TO_DELIVERYMAN" },
  { label: "Await Delivery Man", value: "AWAITDELIVERYMAN" },
  { label: "Picked Up", value: "PICKED_UP" },
  { label: "Out for Delivery", value: "OUT FOR DELIVERY" },
  { label: "Delivered", value: "DELIVERED" },
  { label: "Cancelled", value: "CANCELLED" },
  { label: "Rejected", value: "REJECTED" },
  { label: "Return Requested", value: "RETURN_REQUESTED" },
  { label: "Accepted Return", value: "ACCEPTED_RETURN" },
  { label: "Returned", value: "RETURNED" },
  { label: "Replaced", value: "REPLACED" },
];

const PAYMENT_STATUS_OPTIONS = [
  { label: "All Payment Status", value: "" },
  { label: "Paid", value: "PAID" },
  { label: "Unpaid", value: "UNPAID" },
  { label: "Pending", value: "PENDING" },
  { label: "Failed", value: "FAILED" },
];

const PAY_TYPE_OPTIONS = [
  { label: "All Pay Types", value: "" },
  { label: "Cash (COD)", value: "CASH" },
  { label: "Card", value: "CARD" },
  { label: "Online", value: "ONLINE" },
];

const STATUS_COLORS = {
  DELIVERED: "bg-green-100 text-green-700",
  WAITING: "bg-yellow-100 text-yellow-700",
  ACCEPTED: "bg-blue-100 text-blue-700",
  ASSIGNED: "bg-indigo-100 text-indigo-700",
  ASSIGNED_TO_DELIVERYMAN: "bg-indigo-100 text-indigo-700",
  AWAITDELIVERYMAN: "bg-purple-100 text-purple-700",
  PICKED_UP: "bg-cyan-100 text-cyan-700",
  "OUT FOR DELIVERY": "bg-cyan-100 text-cyan-700",
  CANCELLED: "bg-red-100 text-red-700",
  REJECTED: "bg-red-100 text-red-700",
  RETURN_REQUESTED: "bg-orange-100 text-orange-700",
  ACCEPTED_RETURN: "bg-orange-100 text-orange-700",
  RETURNED: "bg-gray-100 text-gray-700",
  REPLACED: "bg-teal-100 text-teal-700",
};

// ─── Main Component ────────────────────────────────────────────────────────────

const METHOD_COLORS = {
  CARD: "bg-blue-100 text-blue-700",
  BANK: "bg-purple-100 text-purple-700",
  CASH: "bg-green-100 text-green-700",
};

const STATUS_BADGE = {
  COMPLETED: "bg-green-100 text-green-700",
  PENDING: "bg-yellow-100 text-yellow-700",
  FAILED: "bg-red-100 text-red-700",
};

const VendorPayoutHistoryModal = ({ vendorId, vendorInfo, onClose }) => {
  const [payouts, setPayouts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    totalCount: 0,
  });


  const fetchPayouts = async (page = 1) => {
    setLoading(true);
    try {
      const response = await earningApis.getVendorPayoutById({
        vendor_id: vendorId,
        page,
        limit: 10,
        sortOrder: "desc",
      });
      const data = response?.data?.data || response?.data;
      setPayouts(data?.payouts || []);
      setSummary(data?.summary || null);
      setPagination((prev) => ({
        ...prev,
        page,
        totalPages: data?.totalPages || 1,
        totalCount: data?.totalCount || 0,
      }));
    } catch (error) {
      console.error("Failed to fetch payout history:", error);
      toast.error("Failed to load payout history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (vendorId) fetchPayouts(1);
  }, [vendorId]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 overflow-y-auto">
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-4xl mx-4 my-8">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h2 className="text-xl font-bold text-gray-800">
              Payout History — {vendorInfo?.first_name||payouts[0]?.vendor_id?.first_name}{" "}
              {vendorInfo?.last_name || payouts[0]?.vendor_id?.last_name|| ""}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">{vendorInfo?.email||payouts[0]?.vendor_id?.email|| ""}</p>
            <p className="text-sm text-gray-500 mt-0.5">{vendorInfo?.phone_number||payouts[0]?.vendor_id?.phone_number|| ""}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-3 gap-4 px-6 py-4 bg-gray-50 border-b">
            <div className="bg-white rounded-xl border p-4">
              <p className="text-xs text-gray-500 mb-1">Total Paid Out</p>
              <p className="text-xl font-bold text-green-600">
                {formattedAmount(summary.totalPaidOut)}
              </p>
            </div>
            <div className="bg-white rounded-xl border p-4">
              <p className="text-xs text-gray-500 mb-1">Total Payouts</p>
              <p className="text-xl font-bold text-gray-800">
                {summary.totalPayouts}
              </p>
            </div>
            <div className="bg-white rounded-xl border p-4">
              <p className="text-xs text-gray-500 mb-1">Total Orders Paid</p>
              <p className="text-xl font-bold text-blue-600">
                {summary.totalOrders}
              </p>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-gray-400">
              <svg
                className="animate-spin h-8 w-8 mr-3"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8z"
                />
              </svg>
              Loading payout history…
            </div>
          ) : payouts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-12 w-12 mb-3 opacity-30"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
              <p className="text-sm">No payout history found</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto rounded-xl border">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                        #
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                        Date
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                        Orders
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                        Amount
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                        Method
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                        Status
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                        Reference
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                        Paid By
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {payouts.map((payout, index) => (
                      <tr
                        key={payout._id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-4 py-3 text-gray-500 font-mono text-xs">
                          {(pagination.page - 1) * 10 + index + 1}
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          {payout.payout_date
                            ? new Date(payout.payout_date).toLocaleDateString(
                                "en-GB",
                                {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                },
                              )
                            : "—"}
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-semibold text-gray-800">
                            {payout.orders?.length || 0}
                          </span>
                          <span className="text-gray-400 text-xs ml-1">
                            orders
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-bold text-green-600">
                            {formattedAmount(payout.total_amount)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`text-xs px-2 py-1 rounded-full font-medium ${METHOD_COLORS[payout.payment_method] || "bg-gray-100 text-gray-600"}`}
                          >
                            {payout.payment_method}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`text-xs px-2 py-1 rounded-full font-medium ${STATUS_BADGE[payout.status] || "bg-gray-100 text-gray-600"}`}
                          >
                            {payout.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-gray-500">
                          {payout.transaction_reference
                            ? payout.transaction_reference.slice(-10)
                            : "—"}
                        </td>
                        <td className="px-4 py-3 text-gray-700 text-xs">
                          {payout.paid_by
                            ? `${payout.paid_by.first_name || ""} ${payout.paid_by.last_name || ""}`.trim() ||
                              payout.paid_by.email
                            : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-gray-500">
                    Page {pagination.page} of {pagination.totalPages} (
                    {pagination.totalCount} total)
                  </p>
                  <div className="flex gap-2">
                    <button
                      disabled={pagination.page <= 1}
                      onClick={() => fetchPayouts(pagination.page - 1)}
                      className="px-3 py-1.5 text-sm rounded-lg border hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      disabled={pagination.page >= pagination.totalPages}
                      onClick={() => fetchPayouts(pagination.page + 1)}
                      className="px-3 py-1.5 text-sm rounded-lg border hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const VendorProductEarnings = ({ meta }) => {
  const { vendorId } = useParams();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("");
  const [showPaystack, setShowPaystack] = useState(false);
  // activeFilters holds the UI state (including dateFrom/dateTo for inputs)
  const [showPayoutHistory, setShowPayoutHistory] = useState(false);
  const [activeFilters, setActiveFilters] = useState({});

  const [pagination, setPagination] = useState({
    page: 1,
    first: 0,
    limit: 10,
    sortOrder: "desc",
    filter: {},
  });
  const [selectedData, setSelectedData] = useState(null);
  const debounceTimeout = useRef(null);

  const DataFilter = filters;

  const handleFilter = (name, value) => {
    console.log("handleFilter called =>", name, value);

    let updatedFilter = { ...pagination.filter };

    if (name === "search_by") {
      setPagination((prev) => ({
        ...prev,
        sortOrder: value?.code || "desc",
        page: 1,
      }));
      return;
    }

    const normalize = (val) => {
      if (!val && val !== 0) return undefined;
      if (typeof val === "string") return val || undefined;
      if (val.code) return val.code;
      if (val.$eq) return val.$eq;
      return undefined;
    };

    const normalized = normalize(value);

    const filterFields = [
      "payment_status",
      "pay_type",
      "order_type",
      "delivery_status",
      "orderDetails.delivery_status", // ← add this
    ];

    if (filterFields.includes(name)) {
      if (normalized) {
        updatedFilter[name] = normalized;
      } else {
        delete updatedFilter[name];
      }
    }

    if (name === "order_date" && Array.isArray(value) && value.length === 2) {
      updatedFilter.order_date = value;
    }

    setPagination((prev) => ({ ...prev, page: 1, filter: updatedFilter }));
  };

  useEffect(() => {
    console.log("FINAL PAGINATION PAYLOAD :", pagination);
  }, [pagination]);

  const payoutTotal = selectedOrders.reduce(
    (sum, item) => sum + Number(item.total_item_price || 0),
    0,
  );

  // Build clean filter object to send to backend from activeFilters
  const buildBackendFilter = (f) => {
    const result = {};

    // DELIVERY STATUS
    if (f.delivery_status) {
      result.delivery_status = f.delivery_status.code || f.delivery_status;
    }

    // PAYMENT STATUS
    if (f.payment_status) {
      result.payment_status = f.payment_status.code || f.payment_status;
    }

    // PAY TYPE
    if (f.pay_type) {
      result.pay_type = f.pay_type.code || f.pay_type;
    }

    // ORDER TYPE
    if (f.order_type) {
      result.order_type = f.order_type.code || f.order_type;
    }

    // DATE RANGE
    if (f.order_date) {
      result.order_date = f.order_date;
    }

    return result;
  };
  console.log("FINAL PAGINATION PAYLOAD", pagination);

  const { value, fetchAllData } = useFetchAll(() =>
    OrderApi.getAllOrders({ ...pagination, vendor_id: vendorId }, search),
  );

  const orders = value?.data?.orders || [];
  const stats = value?.data?.stats || {};
  const vendorInfo = value?.data?.vendor || null;

  const paystackConfig = {
    reference: new Date().getTime().toString(),
    email: vendorInfo?.email || "admin@test.com",
    amount: Number(payoutTotal) * 100, // Paystack uses kobo/pesewas
    currency: "GHS",
    publicKey:
      process.env.REACT_APP_PAYSTACK_PUBLIC_KEY ||
      "pk_test_8889c5e2ffe1b813426f25d007f478a24c152963",
  };

  const handleVendorPayout = async (reference = null) => {
    try {
      const payload = {
        vendor_id: vendorId,
        orders: selectedOrders.map((o) => o._id),
        total_amount: payoutTotal,
        payment_method: selectedPaymentMethod,
        transaction_reference: reference,
      };

      console.log(payload);

      const response = await earningApis.createVendorPayout(payload);

      if (response?.data?.success) {
        toast.success("Payout successful");

        setShowPayoutModal(false);
        setSelectedOrders([]);
        setSelectedPaymentMethod("");
        setShowPaystack(false);

        fetchAllData();
      }
    } catch (error) {
      console.log(error);
      toast.error("Payment failed");
    }
  };
  const handlePaystackSuccess = async (reference) => {
    console.log("Payment Success:", reference);

    await handleVendorPayout(reference?.reference);
  };

  const handlePaystackClose = () => {
    console.log("Payment Closed");
  };

  // ─── Table Columns ───────────────────────────────────────────────────────────

  const columns = [
    {
      field: "select",
      header: "",
      accessor: (row) => {
        const deliveryStatus =
          row.orderDetails?.[0]?.delivery_status || row.delivery_status;

        const isDelivered = deliveryStatus === "DELIVERED";

        const checked = selectedOrders.some((o) => o._id === row._id);

        return (
          <input
            type="checkbox"
            disabled={!isDelivered}
            checked={checked}
            className={!isDelivered ? "cursor-not-allowed opacity-50" : ""}
            onChange={(e) => {
              if (!isDelivered) return;

              if (e.target.checked) {
                setSelectedOrders((prev) => [...prev, row]);
              } else {
                setSelectedOrders((prev) =>
                  prev.filter((o) => o._id !== row._id),
                );
              }
            }}
          />
        );
      },
    },
    {
      field: "order_id",
      header: "Order ID",
      accessor: (row) => (
        <span className="font-mono font-semibold text-gray-800">
          #{row.order_id || "N/A"}
        </span>
      ),
    },
    {
      field: "customerName",
      header: "Customer",
      accessor: (row) => {
        const c = row.customer || row.created_by;
        if (!c) return "-";
        return (
          `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.email || "-"
        );
      },
    },
    {
      field: "productName",
      header: "Product",
      accessor: (row) => row.orderDetails?.[0]?.product?.name || "-",
    },
    {
      field: "order_type",
      header: "Order Type",
      accessor: (row) => (
        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
          {row.order_type || "-"}
        </span>
      ),
    },
    {
      field: "delivery_status",
      header: "Delivery Status",
      accessor: (row) => {
        const status =
          row.orderDetails?.[0]?.delivery_status || row.delivery_status || "-";
        const colorClass = STATUS_COLORS[status] || "bg-gray-100 text-gray-600";
        return (
          <span
            className={`text-xs px-2 py-1 rounded-full font-medium ${colorClass}`}
          >
            {status.replace(/_/g, " ")}
          </span>
        );
      },
    },
    {
      field: "payment_status",
      header: "Payment",
      accessor: (row) => {
        const status = row.payment_status || "-";
        return (
          <span
            className={`text-xs px-2 py-1 rounded-full font-medium ${
              status === "PAID"
                ? "bg-green-100 text-green-700"
                : status === "FAILED"
                  ? "bg-red-100 text-red-700"
                  : "bg-yellow-100 text-yellow-700"
            }`}
          >
            {status}
          </span>
        );
      },
    },
    {
      field: "pay_type",
      header: "Pay Type",
      accessor: (row) => {
        const type = row.pay_type || "-";
        const icons = { CASH: "💵", CARD: "💳", ONLINE: "🌐" };
        return (
          <span className="text-sm">
            {icons[type] || ""} {type}
          </span>
        );
      },
    },
    {
      field: "item_price",
      header: "Item Price",
      accessor: (row) => formattedAmount(row.item_price || 0),
    },
    {
      field: "total_delivery_price",
      header: "Delivery Fee",
      accessor: (row) => formattedAmount(row.total_delivery_price || 0),
    },
    {
      field: "net_total",
      header: "Net Total",
      accessor: (row) => (
        <span className="font-semibold text-blue-700">
          {formattedAmount(row.net_total || 0)}
        </span>
      ),
    },
    {
      field: "order_date",
      header: "Order Date",
      accessor: (row) =>
        row.order_date
          ? new Date(row.order_date).toLocaleDateString("en-IN", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
          : "-",
    },
  ];

  const searchBy = {
    filterType: "Search By",
    inputName: "search_by",
    options: [
      { name: "Order ID", code: "order_id" },
      { name: "Product", code: "product" },
      { name: "Customer", code: "customer" },
    ],
  };

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handlePagination = (data) => {
    setPagination((prev) => ({
      ...prev,
      page: Math.max(1, Number(data.page) || prev.page),
      limit: Math.max(1, Number(data.limit) || prev.limit),
      sortOrder: data.sortOrder ?? prev.sortOrder,
      filter: data.filter ?? prev.filter,
    }));
  };

  const onPaginatorClick = (e) => {
    console.log("Paginator Event =>", JSON.stringify(e, null, 2));
    console.log("Current pagination:", pagination);

    // Handle different possible event structures
    let nextPage;
    if (typeof e === "number") {
      nextPage = e;
    } else if (e?.page !== undefined) {
      nextPage = Number(e.page);
    } else if (e?.first !== undefined && e?.rows) {
      // Some paginators use first/rows pattern
      nextPage = Math.floor(e.first / e.rows);
    } else {
      nextPage = 0;
    }

    const newPage = Math.max(0, nextPage) + 1;

    setPagination((prev) => ({
      ...prev,
      page: newPage,
      limit: Number(e?.rows) || prev.limit,
    }));
  };
  const handleSearch = useCallback((val) => setSearch(val), []);

  const debouncedSearch = useCallback(
    (val) => {
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
      debounceTimeout.current = setTimeout(() => handleSearch(val), 400);
    },
    [handleSearch],
  );

  // When filter bar changes → update pagination.filter and reset to page 1
  const handleFilterChange = (newFilters) => {
    setActiveFilters(newFilters);

    // buildBackendFilter should return plain strings, not objects
    const backendFilter = {};
    if (newFilters.delivery_status?.$eq)
      backendFilter.delivery_status = newFilters.delivery_status.$eq;
    if (newFilters.payment_status)
      backendFilter.payment_status = newFilters.payment_status;
    if (newFilters.pay_type) backendFilter.pay_type = newFilters.pay_type;
    if (newFilters.order_date) backendFilter.order_date = newFilters.order_date;

    setPagination((prev) => ({ ...prev, page: 1, filter: backendFilter }));
  };

  const handleClearFilter = () => {
    setSearch("");
    setActiveFilters({});
    setPagination({ page: 1, limit: 10, sortOrder: "desc", filter: {} });
  };

  useEffect(() => {
    fetchAllData();
  }, [pagination, search]);

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="main-content">
      <CommonLayout />

      <div className="mb-6">
        {/* Back Button */}
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={() => navigate("/app/earnings/store")}
            className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
          >
            ← Back to Store Earnings
          </button>

          {/* ── Payout History Button ── */}
          <button
            onClick={() => setShowPayoutHistory(true)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-700 border border-purple-200 rounded-lg text-sm font-medium hover:bg-purple-100 transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
            Payout History
          </button>
        </div>

        {/* Vendor Info Card */}
        {vendorInfo && (
          <div className="bg-white p-4 rounded-lg shadow-sm border mb-4">
            <h2 className="text-xl font-semibold mb-2">
              {vendorInfo.first_name} {vendorInfo.last_name || ""}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="font-medium">Email:</span> {vendorInfo.email}
              </div>
              <div>
                <span className="font-medium">Phone:</span>{" "}
                {vendorInfo.phone_number || "-"}
              </div>
              <div>
                <span className="font-medium">Role:</span>{" "}
                {vendorInfo.user_role?.name || "-"}
              </div>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-4">
          <div className="bg-white border rounded-xl p-4 shadow-sm">
            <p className="text-xs text-gray-500 mb-1">Total Earnings</p>
            <h3 className="text-xl font-bold text-green-600">
              {formattedAmount(stats.totalEarnings || 0)}
            </h3>
          </div>
          <div className="bg-white border rounded-xl p-4 shadow-sm">
            <p className="text-xs text-gray-500 mb-1">Total Orders</p>
            <h3 className="text-xl font-bold text-gray-800">
              {stats.totalOrders || 0}
            </h3>
          </div>
          <div className="bg-white border rounded-xl p-4 shadow-sm">
            <p className="text-xs text-gray-500 mb-1">Delivered</p>
            <h3 className="text-xl font-bold text-blue-600">
              {stats.deliveredOrders || 0}
            </h3>
          </div>
          <div className="bg-white border rounded-xl p-4 shadow-sm">
            <p className="text-xs text-gray-500 mb-1">Pending</p>
            <h3 className="text-xl font-bold text-orange-500">
              {stats.pendingOrders || 0}
            </h3>
          </div>
          <div className="bg-white border rounded-xl p-4 shadow-sm">
            <p className="text-xs text-gray-500 mb-1">Cancelled</p>
            <h3 className="text-xl font-bold text-red-500">
              {stats.cancelledOrders || 0}
            </h3>
          </div>
          <div className="bg-white border rounded-xl p-4 shadow-sm">
            <p className="text-xs text-gray-500 mb-1">Returned</p>
            <h3 className="text-xl font-bold text-purple-500">
              {stats.returnedOrders || 0}
            </h3>
          </div>
        </div>
      </div>
      {selectedOrders.length > 0 && (
        <div className="flex items-center justify-between bg-white border rounded-xl p-4 mb-4 shadow-sm">
          <div>
            <h3 className="font-semibold text-lg">
              Selected Orders: {selectedOrders.length}
            </h3>

            <p className="text-green-600 font-bold text-xl">
              Total: {formattedAmount(payoutTotal)}
            </p>
          </div>

          <button
            onClick={() => setShowPayoutModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold"
          >
            Payout
          </button>
        </div>
      )}

      {/* Orders Table */}
      <BasicTable
        columns={columns}
        data={orders}
        onPaginatorClick={onPaginatorClick}
        paginatorData={{
          filters: null,
          first: 0,
          multiSortMeta: [],
          page: Math.max(0, pagination.page - 1),
          rows: pagination.limit,
          sortField: null,
          sortOrder: null,
          totalPages: value?.data?.totalPages || 1,
          totalCount: value?.data?.totalCount || 0,
        }}
        SearchBy={searchBy}
        filters={DataFilter}
        handleFilter={(name, value) => handleFilter(name, value)}
        handleSearch={debouncedSearch}
        handleClearFilter={handleClearFilter}
        WeekRange={false}
        MonthRange={false}
        selectedData={selectedData}
        handleRowClick={(row) => setSelectedData(row)}
      />

      {showPayoutModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Vendor Payout</h2>

              <button
                onClick={() => setShowPayoutModal(false)}
                className="text-2xl"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div className="border rounded-lg p-4">
                <p className="text-sm text-gray-500">Total Selected Orders</p>

                <h3 className="text-2xl font-bold text-green-600">
                  {formattedAmount(payoutTotal)}
                </h3>
              </div>

              <div className="space-y-3">
                <label className="font-medium">Select Payment Method</label>

                <div className="flex flex-col gap-3">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="payment"
                      onChange={() => setSelectedPaymentMethod("CARD")}
                    />
                    Card / Paystack
                  </label>

                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="payment"
                      onChange={() => setSelectedPaymentMethod("BANK")}
                    />
                    Bank Transfer
                  </label>

                  {/* <label className="flex items-center gap-2">
              <input
                type="radio"
                name="payment"
                onChange={() => setSelectedPaymentMethod("CASH")}
              />
              Cash
            </label> */}
                </div>
              </div>

              {selectedPaymentMethod === "CARD" ? (
                <PaystackButton
                  {...paystackConfig}
                  text="Pay With Paystack"
                  onSuccess={handlePaystackSuccess}
                  onClose={handlePaystackClose}
                  className="w-full py-3 rounded-lg text-white font-semibold bg-green-600 hover:bg-green-700"
                />
              ) : (
                <button
                  onClick={() => handleVendorPayout()}
                  disabled={!selectedPaymentMethod}
                  className={`w-full py-3 rounded-lg text-white font-semibold ${
                    selectedPaymentMethod
                      ? "bg-green-600 hover:bg-green-700"
                      : "bg-gray-300 cursor-not-allowed"
                  }`}
                >
                  Proceed Payment
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Payout History Modal */}
      {showPayoutHistory && (
        <VendorPayoutHistoryModal
          vendorId={vendorId}
          vendorInfo={vendorInfo}
          onClose={() => setShowPayoutHistory(false)}
        />
      )}
    </div>
  );
};

export default VendorProductEarnings;
